import { inject, Injectable } from '@angular/core';
import { HttpContext, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import {
  CreateFundOnHoldRequest,
  FundOnHold,
  FundsOnHoldList,
  FundsOnHoldListRaw,
  FundsOnHoldListRequest,
  UpdateFundOnHoldRequest,
} from '@shared/models';
import { BackendService } from '../backend/backend.service';
import { IS_EXTENDED_ERRORS } from '@shared/interceptors';

@Injectable({
  providedIn: 'root',
})
export class FundsOnHoldService {
  backendService = inject(BackendService);

  private baseUrl = '/v1/funds-on-hold';

  getFundsOnHold(requestParams: FundsOnHoldListRequest): Observable<FundsOnHoldList> {
    let params = new HttpParams();

    if (requestParams.page !== undefined) {
      params = params.set('page', requestParams.page);
    }
    if (requestParams.size) {
      params = params.set('size', requestParams.size);
    }
    if (requestParams.financialAccountId) {
      params = params.set('financialAccountId', requestParams.financialAccountId);
    }
    if (requestParams.status) {
      params = params.set('status', requestParams.status);
    }
    if (requestParams.externalRefId) {
      params = params.set('externalRefId', requestParams.externalRefId);
    }
    if (requestParams.releasedTxnId) {
      params = params.set('releasedTxnId', requestParams.releasedTxnId);
    }
    if (requestParams.expiryFrom) {
      params = params.set('expiryFrom', requestParams.expiryFrom);
    }
    if (requestParams.expiryTo) {
      params = params.set('expiryTo', requestParams.expiryTo);
    }
    if (requestParams.amountFrom !== undefined) {
      params = params.set('amountFrom', requestParams.amountFrom);
    }
    if (requestParams.amountTo !== undefined) {
      params = params.set('amountTo', requestParams.amountTo);
    }

    if (requestParams.sortParams) {
      const { sortProp, sortDir } = requestParams.sortParams;
      if (sortProp && sortDir) {
        params = params.set('sort', `${sortProp},${sortDir}`);
      }
    }

    return this.backendService.get<FundsOnHoldListRaw>(this.baseUrl, { params }).pipe(
      map((response) => {
        const { content = [], totalElements = 0, totalPages, numberOfElements, number } = response ?? {};
        return { content, totalElements, totalPages, numberOfElements, number } as FundsOnHoldList;
      }),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getFundOnHoldDetails(id: string): Observable<FundOnHold> {
    return this.backendService.get<FundOnHold>(`${this.baseUrl}/${id}`).pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  createFundOnHold(data: CreateFundOnHoldRequest): Observable<FundOnHold> {
    return this.backendService.post<FundOnHold>(this.baseUrl, data, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  updateFundOnHold(id: string, data: UpdateFundOnHoldRequest): Observable<FundOnHold> {
    return this.backendService.update<FundOnHold>(`${this.baseUrl}/${id}`, data, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }
}
